import Swiper from 'swiper'
import { FreeMode, Mousewheel, Navigation } from 'swiper/modules'
import { onPage } from '../app/lifecycle'
import { getState } from '../app/store'

/**
 * Menu block, display "slider" (template-parts/blocks/block-menu-column.php):
 * every menu post (Pizze, Bibite, …) is its own horizontal row of item cards.
 *
 * The server renders the plain list. With JS and effects on
 * (html.js:not(.is-easy-mode)) the CSS already lays it out as a row of cards;
 * here that row becomes a Swiper (drag, trackpad, prev/next). Swiper runs on
 * the theme's class names (menu-slider-track / menu-item) without its own
 * stylesheet — 85_blocks.sass has what it needs. Easy mode keeps the stacked
 * list; the page is remounted when it toggles, and cleanup restores the markup.
 *
 * Hooks: [data-menu-slider] > [data-menu-slider-viewport] > [data-menu-slider-track] > .menu-item
 *        [data-menu-slider-nav] [data-menu-slider-prev] [data-menu-slider-next]
 */
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

function enhance(slider) {
  const viewport = slider.querySelector('[data-menu-slider-viewport]')
  const track = viewport?.querySelector('[data-menu-slider-track]')
  if (!track) return null

  const nav = slider.querySelector('[data-menu-slider-nav]')
  const prevEl = slider.querySelector('[data-menu-slider-prev]')
  const nextEl = slider.querySelector('[data-menu-slider-next]')

  // Swiper spaces the slides itself (inline margins), so hand it the gap the
  // CSS row uses — the row drops its own gap once Swiper is initialised.
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0
  const still = reducedMotion()

  if (nav) nav.hidden = false

  const swiper = new Swiper(viewport, {
    modules: [FreeMode, Mousewheel, Navigation],
    wrapperClass: 'menu-slider-track',
    slideClass: 'menu-item',
    slidesPerView: 'auto',
    spaceBetween: gap,
    speed: still ? 0 : 450,
    grabCursor: true,
    watchOverflow: true,
    freeMode: { enabled: true, momentum: !still },
    mousewheel: { forceToAxis: true, releaseOnEdges: true },
    navigation: {
      prevEl,
      nextEl,
      addIcons: false,
      disabledClass: 'is-disabled',
      lockClass: 'is-locked',
    },
  })

  // At either end of the row Swiper sets `disabled` on that <button>. A focused
  // button that becomes disabled drops focus to <body>, so a keyboard user
  // pressing "weiter" would lose their place at the last card. Hand focus to
  // the opposite button. This runs after Swiper's own toEdge handler (modules
  // subscribe first). Browsers only fix up focus at the next rendering step,
  // so the disabled button still reports as focused here. Drag and trackpad
  // users are left alone: they never had focus on a button.
  // swiper.destroy() removes the listener.
  swiper.on('toEdge', () => {
    const active = document.activeElement
    const other = active === nextEl ? prevEl : active === prevEl ? nextEl : null
    if (active?.disabled && other && !other.disabled) other.focus({ preventScroll: true })
  })

  return () => {
    swiper.destroy(true, true)
    ;[prevEl, nextEl].forEach((button) => {
      if (!button) return
      button.disabled = false
      button.classList.remove('is-disabled', 'is-locked')
    })
    if (nav) nav.hidden = true
  }
}

onPage((root) => {
  if (getState().easyMode) return undefined

  const cleanups = [...root.querySelectorAll('[data-menu-slider]')].map(enhance).filter(Boolean)
  if (!cleanups.length) return undefined

  return () => cleanups.forEach((cleanup) => cleanup())
})
